import React, { useEffect, useState, useCallback, useRef } from "react";
import { pausableInterval } from "@/lib/appVisibility";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUserBlocked } from "@/lib/blocks";
import { useVolumeKeyUnmute, VOLUME_PRIORITY } from "@/lib/volumeKeys";
import { prefetchedGet } from "@/lib/bootPrefetch";
import { guardOn } from "@/lib/guards";
import { isOffline } from "@/lib/netStatus";
import { readCachedStories, writeCachedStories } from "@/lib/offlineCache";
import { freshStoryGroups } from "@/lib/instantBoot";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Plus, Play, Send, ChevronLeft, ChevronRight, Eye, EyeOff, X, Trash2, Church, ShieldCheck, Volume2, VolumeX, Archive, Flag, Star } from "lucide-react";
import { toast } from "sonner";
import { resolveMediaUrl } from "@/components/FileUpload";
import { avatarSrc } from "@/lib/defaults";
import { prettyMusicTitle } from "@/lib/musicTitle"; // lot 680 — titre de musique lisible, coupable sur 2 lignes
import { createMusicFader } from "@/lib/audioFade";
import { ReportDialog } from "@/components/ReportDialog";
import { StoryComposer } from "@/components/StoryComposer";
import { StoryArchive } from "@/components/StoryArchive";
import { StoryPrivateScreen } from "@/components/story/StoryPrivateScreen";
import { StoryUploadTiles } from "@/context/UploadQueueContext";
import { StoryCover, StoryStage } from "@/components/StoryStage";
import { useBackHandler } from "@/lib/backButton";
import { escapeConsumed } from "@/lib/escapeLayer";
import { imageVariant } from "@/lib/mediaUrl";
import { schedulePrefetch, upcomingStories } from "@/lib/storyPrefetch";
import { storyTimeLabel } from "@/lib/timeAgo";
import { requireCompleteProfile } from "@/lib/profileDone";
import { isMusicTimedStory, musicStorySeconds, useMusicStoryProgress } from "@/lib/musicStoryProgress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody } from "@/components/ui/dialog";
import { AudienceOptions } from "@/components/AudienceOptions";
import { StoryHiddenFromLink, StoryHiddenFromDialog } from "@/components/story/StoryHiddenFrom";
import { audienceOf, audienceAccent, writeDefaultStoryAudience, CLOSE_FRIENDS_COLOR } from "@/lib/postAudience";

// Lot 737 — mêmes réactions que les publications (👍 ❤️ 🙏 😢 😂 😮, sans 🙌) : « les gens recherchent pleurer, rire, ok… ».
const QUICK_REACTIONS = ["👍", "❤️", "🙏", "😢", "😂", "😮"];
const STORY_DURATION_MS = 5000;

export const StoryBar = () => {
  const { user, t, lang, config, reconnecting } = useApp();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userRef = useRef(null);
  userRef.current = user;
  const musicRef = useRef(null);
  const musicStoryRef = useRef(null); // lot 619 — id de la story dont la musique est en cours (remise au départ à chaque story)
  const [groups, setGroups] = useState([]);
  useUserBlocked(useCallback((uid) => setGroups((arr) => arr.filter((g) => g.author?.id !== uid && g.author_id !== uid)), []));
  const [viewer, setViewer] = useState(null); // {gIdx, sIdx}
  const [composerOpen, setComposerOpen] = useState(false);
  // Lot 597 — profil incomplet : la story ne se crée pas, la feuille « Complète ton profil » s'ouvre à la place.
  useEffect(() => { if (composerOpen && !requireCompleteProfile(user)) setComposerOpen(false); }, [composerOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  const [reply, setReply] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const sendingRef = useRef(false);
  const [paused, setPaused] = useState(false);
  // Curseur « Avant / Après » d'une carte partagée : le glissement met la story en pause (événement de BeforeAfterAuto).
  useEffect(() => {
    const onHold = (e) => setPaused(!!e.detail?.held);
    window.addEventListener("yalola:story-hold", onHold);
    return () => window.removeEventListener("yalola:story-hold", onHold);
  }, []);
  const [videoPct, setVideoPct] = useState(0);
  const [viewersPanel, setViewersPanel] = useState(null); // {loading, viewers}
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [audienceOpen, setAudienceOpen] = useState(false); // lot 651 — feuille « Qui peut voir ta story ? »
  const [audienceBusy, setAudienceBusy] = useState(false);
  const [hiddenOpen, setHiddenOpen] = useState(false); // lot 652 — « Masquer ma story à »
  const [reporting, setReporting] = useState(false);
  const [storyMuted, setStoryMuted] = useState(true);
  // Lot 638 — visionneuse ouverte + touche de volume physique → son de la story activé (prioritaire sur le fil dessous).
  useVolumeKeyUnmute(!!viewer, VOLUME_PRIORITY.STORY, () => setStoryMuted(false));
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [reactionPicker, setReactionPicker] = useState(false);
  const [floaters, setFloaters] = useState([]);
  const tileVideoRefs = useRef({});
  const holdTimerRef = useRef(null);
  const gestureRef = useRef(null);
  const cardRef = useRef(null);
  const viewerVideoRef = useRef(null);

  // Android hardware back button: close the innermost story overlay instead of exiting the app.
  useBackHandler(archiveOpen, () => { setArchiveOpen(false); return true; });
  useBackHandler(composerOpen, () => { setComposerOpen(false); return true; });
  useBackHandler(!!viewer, () => {
    if (reactionPicker) { setReactionPicker(false); setPaused(false); return true; }
    if (viewersPanel) { setViewersPanel(null); return true; }
    if (confirmDelete) { setConfirmDelete(false); setPaused(false); return true; }
    setViewer(null);
    return true;
  });

  const [storiesLoaded, setStoriesLoaded] = useState(false);
  // Lot 713 — mode dégradé : dernières stories gardées sur l'appareil (garde-fou « net_resilience ») ; hors ligne ou
  // serveur injoignable, elles s'affichent tout de suite au lieu d'un bandeau vide, puis la vraie liste les remplace.
  // Lot 717 — démarrage instantané (garde-fou « instant_boot ») : même amorce à l'ouverture, en ligne aussi (stories
  // encore valables seulement) ; la liste du serveur la remplace en silence.
  const showCachedStories = () => {
    const uid = userRef.current?.id;
    if (!uid || !guardOn("net_resilience")) return false;
    const c = readCachedStories(uid);
    const fresh = c ? freshStoryGroups(c.groups) : [];
    if (!fresh.length) return false;
    setGroups((cur) => (cur.length ? cur : fresh));
    setStoriesLoaded(true);
    return true;
  };
  // Visionneuse ouverte (story du cache tapée avant la réponse du serveur, ou story publiée pendant la lecture) : la
  // liste fraîche est appliquée à la fermeture — ses index gIdx/sIdx pointeraient sinon sur une autre story.
  const viewerRef = useRef(null);
  viewerRef.current = viewer;
  const pendingGroupsRef = useRef(null);
  useEffect(() => {
    if (viewer || !pendingGroupsRef.current) return;
    setGroups(pendingGroupsRef.current);
    pendingGroupsRef.current = null;
  }, [viewer]);
  const load = () => prefetchedGet("/stories").then(({ data }) => {
    const g = data.stories || [];
    const uid = userRef.current?.id;
    if (uid) g.sort((a, b) => ((b.author?.id === uid ? 1 : 0) - (a.author?.id === uid ? 1 : 0)));
    if (viewerRef.current) pendingGroupsRef.current = g; else setGroups(g);
    setStoriesLoaded(true);
    if (uid && guardOn("net_resilience")) writeCachedStories(uid, g);
    window.dispatchEvent(new Event("yalola:stories_updated"));
  }).catch(() => { showCachedStories(); });
  useEffect(() => {
    if (reconnecting || isOffline() || guardOn("instant_boot")) showCachedStories();
    load();
    const onCreated = () => load();
    window.addEventListener("yalola:story_created", onCreated);
    return () => window.removeEventListener("yalola:story_created", onCreated);
  }, []);

  const hasStoryDraft = (() => { try { return !!localStorage.getItem("yalola.story_draft"); } catch { return false; } })();

  // Rangée des stories hors écran → aperçus vidéo en pause (sinon un décodeur était réveillé toutes les 2 s en arrière-plan).
  const [rowVisible, setRowVisible] = useState(true);
  const scrollRef = useRef(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(([e]) => setRowVisible(!!e?.isIntersecting), { threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Video tiles preview 1-2s each, in turn — differentiates video stories from photo stories.
  useEffect(() => {
    const refs = tileVideoRefs.current;
    const vids = groups.map((g, i) => (g.stories?.[0]?.media_type === "video" ? i : -1)).filter((i) => i >= 0);
    if (vids.length === 0 || !rowVisible) return;
    let pos = 0;
    const tick = () => {
      const activeIdx = vids[pos];
      Object.entries(refs).forEach(([k, el]) => {
        if (!el) return;
        if (Number(k) === activeIdx) {
          try { el.currentTime = 0; el.play().catch(() => {}); } catch { /* noop */ }
        } else {
          try { el.pause(); } catch { /* noop */ }
        }
      });
      pos = (pos + 1) % vids.length;
    };
    tick();
    const stop = pausableInterval(tick, 2000); // lot 699 — aperçus en pause quand l'app est cachée, relancés au retour
    return () => {
      stop();
      Object.values(refs).forEach((el) => { try { el?.pause(); } catch { /* noop */ } });
    };
  }, [groups, rowVisible]);

  const group = viewer ? groups[viewer.gIdx] : null;
  const story = group?.stories?.[viewer.sIdx] || null;
  const isOwn = !!(story && user && story.author_id === user.id);
  const musicTimed = isMusicTimedStory(story); // lot 619 — photo/texte + musique : durée calée sur la musique (≤ réglage admin)

  const closeViewer = () => { setViewer(null); setViewersPanel(null); setReply(""); setPaused(false); setConfirmDelete(false); load(); };
  const openStory = (gIdx) => { setReply(""); setViewersPanel(null); setPaused(false); setVideoPct(0); setConfirmDelete(false); setViewer({ gIdx, sIdx: 0 }); };

  // Deep-link: /home?story=<groupId>[&sid=<storyId>] opens that story group directly (from a
  // notification tap or a chat context card). groupId = church_id for church stories, author_id otherwise.
  const [privateStory, setPrivateStory] = useState(null); // lot 523 : story d'un compte privé non suivi → écran dédié
  useEffect(() => {
    const target = searchParams.get("story");
    if (!target || !storiesLoaded) return;
    const idx = groups.findIndex((g) => g.author?.id === target);
    const sid = searchParams.get("sid");
    const localIdx = idx >= 0 && sid ? (groups[idx].stories || []).findIndex((s) => s.id === sid) : -1;
    const sp = new URLSearchParams(searchParams);
    sp.delete("story");
    sp.delete("sid");
    if (idx >= 0 && (!sid || localIdx >= 0)) {
      openStory(idx);
      if (localIdx > 0) setViewer({ gIdx: idx, sIdx: localIdx });
    } else {
      const gone = () => toast.info(t("share.story_gone", "Cette story n'est plus disponible"), { id: "share-notice-story_gone" });
      // Story introuvable dans la rangée : compte privé non suivi → écran « Ce compte est privé » ; sinon expirée/masquée.
      const peek = () => api.get(`/stories/${sid}/peek`).then(({ data }) => { if ((data.status === "private" || data.status === "audience") && data.author) setPrivateStory({ ...data.author, reason: data.status, audience: data.audience }); else gone(); }).catch(gone);
      // Lot 602 — chargement DIRECT du groupe (auteur non suivi, > 200 stories, modérateur sur un profil privé ou une
      // story masquée — même si l'auteur est déjà dans la rangée, la story masquée n'y figure pas) : le groupe est
      // inséré/remplacé en tête de rangée et la story s'ouvre, au lieu d'un « n'est plus disponible » à tort.
      if (sid) {
        api.get(`/stories/${sid}/group`).then(({ data }) => {
          const g = data?.group;
          if (!g?.stories?.length) { peek(); return; }
          const sIdx = Math.max(0, g.stories.findIndex((s) => s.id === sid));
          setGroups((prev) => [g, ...prev.filter((x) => x.author?.id !== g.author?.id)]);
          setReply(""); setViewersPanel(null); setPaused(false); setVideoPct(0); setConfirmDelete(false);
          setViewer({ gIdx: 0, sIdx });
        }).catch(peek);
      } else gone();
    }
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, groups, storiesLoaded]);

  const openReport = () => { setPaused(true); setReportOpen(true); };
  const doReportStory = async (reason, category) => {
    if (!story || reporting) return;
    setReporting(true);
    try {
      await api.post(`/stories/${story.id}/report`, { reason, category });
      toast.success(
        category === "child_safety"
          ? t("report.csae_toast", "Signalement urgent transmis à notre équipe de sécurité. Merci de protéger notre communauté.")
          : t("story.reported_toast", "Merci, cette story a été signalée aux modérateurs")
      );
      setReportOpen(false);
      if (category === "child_safety") closeViewer();
      else setPaused(false);
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setReporting(false); }
  };

  const next = useCallback(() => {
    setViewersPanel(null); setVideoPct(0); setConfirmDelete(false);
    setViewer((v) => {
      if (!v) return v;
      const g = groups[v.gIdx];
      if (v.sIdx + 1 < (g?.stories?.length || 0)) return { ...v, sIdx: v.sIdx + 1 };
      if (v.gIdx + 1 < groups.length) return { gIdx: v.gIdx + 1, sIdx: 0 };
      return null;
    });
  }, [groups]);

  const prev = useCallback(() => {
    setViewersPanel(null); setVideoPct(0); setConfirmDelete(false);
    setViewer((v) => {
      if (!v) return v;
      if (v.sIdx > 0) return { ...v, sIdx: v.sIdx - 1 };
      if (v.gIdx > 0) {
        const pg = groups[v.gIdx - 1];
        return { gIdx: v.gIdx - 1, sIdx: Math.max(0, (pg?.stories?.length || 1) - 1) };
      }
      return v;
    });
  }, [groups]);

  // Record the view + auto-advance timer (images & text; videos advance on ended, music stories on the music — lot 619).
  useEffect(() => {
    if (!viewer || !story) return;
    setReply("");
    if (user && story.author_id !== user.id) {
      api.post(`/stories/${story.id}/view`).catch(() => {});
    }
    if (story.media_type === "video" || musicTimed || paused) return;
    const tm = setTimeout(next, STORY_DURATION_MS);
    return () => clearTimeout(tm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewer?.gIdx, viewer?.sIdx, paused]);

  // Lot 619 — story photo/texte + musique : la barre suit la musique et la story avance quand l'extrait (≤ limite admin) est joué.
  const musicPct = useMusicStoryProgress({
    active: !!viewer && musicTimed, audioRef: musicRef, storyKey: story?.id, startAt: story?.music_start || 0,
    limitSec: musicStorySeconds(config), paused, onDone: next,
  });

  // Lot 593 — précharge la story suivante (+ la 1re du groupe suivant) pendant que celle-ci est à l'écran.
  useEffect(() => {
    if (!viewer || !story) return undefined;
    return schedulePrefetch(upcomingStories(groups, viewer.gIdx, viewer.sIdx));
  }, [viewer?.gIdx, viewer?.sIdx, groups]); // eslint-disable-line react-hooks/exhaustive-deps

  // Story music overlay: play the attached clip while the story is on screen,
  // at the chosen volume/start, with a gentle fade in/out on each loop.
  useEffect(() => {
    const a = musicRef.current;
    if (!a) return undefined;
    if (viewer && story?.music_url) {
      const src = resolveMediaUrl(story.music_url);
      const startAt = story.music_start || 0;
      if (a.getAttribute("data-src") !== src) { a.src = src; a.setAttribute("data-src", src); a.currentTime = startAt; }
      else if (musicStoryRef.current !== story.id) { try { a.currentTime = startAt; } catch { /* noop */ } } // même musique, nouvelle story → repart du début
      musicStoryRef.current = story.id;
      a.loop = !startAt;
      a.muted = storyMuted;
      a.onended = startAt ? () => { try { a.currentTime = startAt; a.play().catch(() => {}); } catch { /* noop */ } } : null;
      const fader = createMusicFader(a, {});
      fader.setTarget(typeof story.music_volume === "number" ? story.music_volume : 1);
      fader.setStart(startAt);
      fader.setGate(1);
      if (!paused) a.play().catch(() => {});
      else a.pause();
      return () => { fader.destroy(); };
    }
    try { a.pause(); a.removeAttribute("src"); a.removeAttribute("data-src"); } catch { /* noop */ }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewer?.gIdx, viewer?.sIdx, story?.music_url, story?.music_start, story?.music_volume, storyMuted, paused]);

  // Long-press pause also pauses the currently playing story video.
  // Lot 628 : la reprise ne relance la lecture que si elle était en pause (pas au changement de story : StoryStage
  // attend d'abord un tampon suffisant pour éviter les à-coups sur réseau lent).
  const prevPausedRef = useRef(paused);
  useEffect(() => {
    const v = viewerVideoRef.current;
    const wasPaused = prevPausedRef.current;
    prevPausedRef.current = paused;
    if (!v) return;
    if (paused) { try { v.pause(); } catch { /* noop */ } }
    else if (wasPaused && v.dataset?.buffering !== "1") { v.play?.().catch(() => {}); }
  }, [paused, viewer?.gIdx, viewer?.sIdx]);

  // Drive the VIDEO story progress bar by polling the <video> element directly.
  // Relying on the onTimeUpdate event + currentTime/duration is unreliable on the
  // Android WebView (event can be throttled/never fire, and some videos report a
  // non-finite duration → the bar stayed stuck at 0). A rAF loop fills it smoothly on every device.
  // Lot 618 — les clips découpés sur le téléphone (MediaRecorder) n'ont pas de durée en en-tête (duration = Infinity) :
  // la barre se cale alors sur `story.video_duration` (durée réelle enregistrée à la publication) et non plus sur un
  // minuteur fixe de 15 s qui finissait avant la vidéo.
  useEffect(() => {
    if (!viewer || story?.media_type !== "video" || paused) return;
    let raf = 0, startTs = null, last = -1;
    const known = Number(story?.video_duration) > 0 ? Number(story.video_duration) : 0;
    const FALLBACK_MS = known ? known * 1000 : 15000;
    const loop = (ts) => {
      const v = viewerVideoRef.current;
      if (v) {
        const d = Number.isFinite(v.duration) && v.duration > 0 ? v.duration : known;
        let pct;
        if (d) {
          pct = Math.min(100, (v.currentTime / d) * 100);
        } else {
          if (startTs == null) startTs = ts;
          pct = Math.min(100, ((ts - startTs) / FALLBACK_MS) * 100);
        }
        if (Math.abs(pct - last) >= 0.4) { last = pct; setVideoPct(pct); }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [viewer?.gIdx, viewer?.sIdx, story?.media_type, story?.video_duration, paused]);

  // Desktop keyboard: ← / → navigate, Escape closes (inner panels close first).
  useEffect(() => {
    if (!viewer) return undefined;
    const onKey = (e) => {
      if (reportOpen || audienceOpen || hiddenOpen || escapeConsumed(e)) return; // a Radix dialog above (report, audience, share…) handled this Escape
      const tag = (e.target?.tagName || "").toLowerCase();
      const typing = tag === "input" || tag === "textarea" || e.target?.isContentEditable;
      if (e.key === "Escape") {
        e.preventDefault();
        if (confirmDelete) { setConfirmDelete(false); setPaused(false); return; }
        if (reactionPicker) { setReactionPicker(false); setPaused(false); return; }
        if (viewersPanel) { setViewersPanel(null); return; }
        closeViewer();
        return;
      }
      if (typing || confirmDelete || reactionPicker) return;
      if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); next(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewer, reportOpen, audienceOpen, hiddenOpen, confirmDelete, reactionPicker, viewersPanel, prev, next]);

  const openViewers = async () => {
    if (!story) return;
    setViewersPanel({ loading: true, viewers: [] });
    try {
      const { data } = await api.get(`/stories/${story.id}/viewers`);
      setViewersPanel({ loading: false, viewers: data.viewers || [] });
    } catch { setViewersPanel(null); }
  };

  const askDelete = () => { setPaused(true); setConfirmDelete(true); };
  // Lot 651 — audience de MA story, modifiable à tout moment depuis la visionneuse (PATCH /stories/{id} {visibility}).
  const openAudience = () => { setPaused(true); setAudienceOpen(true); };
  const closeAudience = (v) => { setAudienceOpen(v); if (!v) setPaused(false); };
  const changeAudience = async (v) => {
    if (!story || v === (story.visibility || "public")) { closeAudience(false); return; }
    setAudienceBusy(true);
    try {
      await api.patch(`/stories/${story.id}`, { visibility: v });
      setGroups((arr) => arr.map((g) => ({ ...g, stories: (g.stories || []).map((s) => (s.id === story.id ? { ...s, visibility: v } : s)) })));
      writeDefaultStoryAudience(v);
      toast.success(t("audience.changed", "Audience modifiée : {label}").replace("{label}", t(audienceOf(v).tkey, audienceOf(v).label)));
      closeAudience(false);
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setAudienceBusy(false); }
  };
  const performDelete = async () => {
    if (!story) return;
    try {
      await api.delete(`/stories/${story.id}`);
      toast.success(t("story.deleted", "Story supprimée"));
      setConfirmDelete(false);
      closeViewer();
      load();
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };

  const sendReply = async (content) => {
    const txt = (content || "").trim();
    // Garde SYNCHRONE (ref) : un double tap arrive avant le re-rendu de `sendingReply` → les deux appels passaient.
    if (!txt || !group?.author?.id || sendingRef.current) return;
    sendingRef.current = true;
    setSendingReply(true);
    try {
      // WhatsApp-style: the story itself travels with the reply (context card in the chat),
      // so the author always knows WHICH story got the reaction — even after it expires.
      const { data } = await api.post("/chat/messages", {
        recipient_id: group.author.id,
        content: txt,
        ref: story?.id ? { type: "story", id: story.id } : undefined,
      });
      window.dispatchEvent(new CustomEvent("yalola:chat", { detail: { type: "sent", conversation_id: data?.conversation_id } }));
      toast.success(t("story.reply_sent", "Réponse envoyée ✓"));
      setReply("");
      // Petite fenêtre après l'envoi : le second tap du réflexe « double tap » ne repart pas (le serveur dédoublonne aussi).
      await new Promise((r) => setTimeout(r, 1200));
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { sendingRef.current = false; setSendingReply(false); }
  };

  // Swipe-up opens the animated reaction picker (only on someone else's story).
  const openReactionPicker = () => {
    if (!user || isOwn) return;
    setPaused(true);
    setReactionPicker(true);
  };
  const pickReaction = (emoji) => {
    const id = Math.random().toString(36).slice(2);
    setFloaters((f) => [...f, { id, emoji }]);
    setTimeout(() => setFloaters((f) => f.filter((x) => x.id !== id)), 1500);
    sendReply(emoji);
    setReactionPicker(false);
    setPaused(false);
  };

  // Unified press gestures on the story: long-press = pause, quick tap L/R =
  // previous/next, swipe up = react (WhatsApp / Instagram style).
  const onPressStart = (e) => {
    if (reactionPicker) return;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
    // Left/right thirds are measured on the story CARD, not the full-width gesture
    // layer: on desktop the card is centred, so a tap on its left edge is at ~45 % of
    // the screen and used to count as "right" (next).
    const card = cardRef.current?.getBoundingClientRect();
    const rect = card && card.width > 0 ? card : e.currentTarget.getBoundingClientRect();
    gestureRef.current = { x: e.clientX, y: e.clientY, left: rect.left, w: rect.width, held: false, swiped: false };
    clearTimeout(holdTimerRef.current);
    holdTimerRef.current = setTimeout(() => {
      if (gestureRef.current) { gestureRef.current.held = true; setPaused(true); }
    }, 260);
  };
  const onPressMove = (e) => {
    const g = gestureRef.current;
    if (!g || g.swiped) return;
    const dy = e.clientY - g.y;
    const dx = Math.abs(e.clientX - g.x);
    if (dy < -55 && dx < 70) {
      g.swiped = true;
      clearTimeout(holdTimerRef.current);
      if (g.held) { g.held = false; setPaused(false); }
      openReactionPicker();
    }
  };
  const onPressEnd = (e) => {
    const g = gestureRef.current;
    gestureRef.current = null;
    clearTimeout(holdTimerRef.current);
    if (!g || g.swiped) return;
    if (g.held) { setPaused(false); return; }
    const x = (Number.isFinite(e.clientX) ? e.clientX : g.left + g.w / 2) - g.left;
    if (x < g.w * 0.33) prev(); else next();
  };
  const onPressCancel = () => {
    const g = gestureRef.current;
    gestureRef.current = null;
    clearTimeout(holdTimerRef.current);
    if (g?.held) setPaused(false);
  };

  // Vignette partagée avec la page d'église (StoryCover) ; la <video> est exposée pour l'aperçu à tour de rôle.
  const renderCover = (st, tileIdx) => <StoryCover story={st} videoRef={(el) => { tileVideoRefs.current[tileIdx] = el; }} />;

  const [arrows, setArrows] = useState({ left: false, right: false });
  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setArrows({ left: scrollLeft > 4, right: scrollLeft + clientWidth < scrollWidth - 4 });
  }, []);
  const scrollByTiles = (dir) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.8), behavior: "smooth" });
  };
  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    window.addEventListener("resize", updateArrows);
    return () => { ro.disconnect(); window.removeEventListener("resize", updateArrows); };
  }, [groups, updateArrows]);

  return (
    <div className="relative" data-testid="stories-bar">
      <div ref={scrollRef} onScroll={updateArrows} className="flex gap-3 overflow-x-auto hide-scrollbar scroll-smooth" data-testid="stories-block">
      {user && (
        <button type="button" onClick={() => setComposerOpen(true)} className="story-tile group text-left" data-testid="create-story">
          <div className="absolute inset-x-0 top-0 h-[62%] overflow-hidden">
            <img src={avatarSrc(user)} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
          </div>
          <div className="absolute bottom-0 inset-x-0 h-[42%] bg-white flex items-end justify-center pb-2.5">
            <span className="text-[11px] font-semibold leading-tight text-center px-1" style={{ color: "var(--yalola-text)" }}>
              {t("story.add", "Créer une story")}
            </span>
          </div>
          <div className="absolute left-1/2 -translate-x-1/2" style={{ top: "54%" }}>
            <div className="h-9 w-9 rounded-full grid place-items-center border-4 border-white" style={{ background: "var(--yalola-primary)" }}>
              <Plus size={17} className="text-white" strokeWidth={3} />
            </div>
          </div>
          {hasStoryDraft && (
            <span
              className="absolute top-1.5 right-1.5 text-[9px] font-bold uppercase tracking-wide rounded-full px-1.5 py-0.5 bg-white/95"
              style={{ color: "var(--yalola-primary)" }}
              data-testid="story-draft-chip"
            >
              {t("composer.draft", "Brouillon")}
            </span>
          )}
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); setArchiveOpen(true); }}
            className="absolute top-1.5 left-1.5 h-6 w-6 rounded-full bg-white/95 grid place-items-center shadow-sm hover:scale-110 transition-transform"
            title={t("story.archive_title", "Mes archives de stories")}
            data-testid="open-story-archive"
          >
            <Archive size={13} style={{ color: "var(--yalola-primary)" }} />
          </span>
        </button>
      )}

      {user && <StoryUploadTiles />}

      {groups.map((g, i) => {
        const first = g.stories?.[0];
        // Lot 655 — anneau vert « Amis proches » : une story du groupe m'est réservée (ou ma propre story en amis proches).
        const closeRing = !g.is_church && (g.stories || []).some((s) => s.visibility === "close_friends") && (g.has_unseen || (user && g.author?.id === user.id));
        return (
          <button
            key={g.author?.id || i}
            className={`story-tile ${closeRing ? "story-tile-close" : g.has_unseen ? "story-tile-new" : (user && g.author?.id === user.id ? "" : "story-tile-seen")}`}
            onClick={() => openStory(i)}
            data-testid={`story-${g.author?.id}`}
            data-close-friends={closeRing ? 1 : 0}
          >
            {renderCover(first, i)}
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.55) 100%)" }} />
            {g.is_church ? (
              <span className="story-new-badge" data-testid="story-announcement-badge">
                {t("story.announcement", "Annonce")}
              </span>
            ) : g.has_unseen ? (
              <span className="story-new-badge" data-testid={`story-new-${g.author?.id}`}>
                {t("story.new", "Nouveau")}
              </span>
            ) : null}
            <div className="story-tile-avatar">
              {g.author?.is_church ? (
                g.author?.avatar_url ? (
                  <img src={imageVariant(g.author.avatar_url, 48)} alt="" className="h-full w-full object-cover" data-testid="story-church-logo" />
                ) : (
                  <div className="h-full w-full rounded-full grid place-items-center" style={{ background: "var(--yalola-primary)" }}>
                    <Church size={16} className="text-white" />
                  </div>
                )
              ) : (
                <Avatar className="h-full w-full">
                  <AvatarImage src={avatarSrc(g.author)} />
                  <AvatarFallback>{g.author?.name?.[0]}</AvatarFallback>
                </Avatar>
              )}
              {g.author?.is_church && (
                <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-white grid place-items-center" data-testid="story-church-badge">
                  <ShieldCheck size={11} style={{ color: "var(--yalola-primary)" }} />
                </span>
              )}
            </div>
            {first?.media_type === "video" && (
              <div className="absolute inset-0 grid place-items-center pointer-events-none">
                <Play size={22} className="text-white/90" />
              </div>
            )}
            <div className="story-tile-name line-clamp-2 leading-tight">{user && g.author?.id === user.id ? t("story.your_story", "Votre story") : g.author?.name}</div>
          </button>
        );
      })}
      </div>

      {arrows.left && (
        <button type="button" onClick={() => scrollByTiles(-1)} aria-label={t("story.prev_stories", "Stories précédentes")}
          className="hidden md:grid absolute left-0 top-[42%] -translate-y-1/2 z-20 h-9 w-9 rounded-full bg-white shadow-md border place-items-center hover:scale-105 transition-transform"
          style={{ borderColor: "var(--yalola-border)" }} data-testid="stories-nav-left">
          <ChevronLeft size={18} style={{ color: "var(--yalola-text)" }} />
        </button>
      )}
      {arrows.right && (
        <button type="button" onClick={() => scrollByTiles(1)} aria-label={t("story.next_stories", "Stories suivantes")}
          className="hidden md:grid absolute right-0 top-[42%] -translate-y-1/2 z-20 h-9 w-9 rounded-full bg-white shadow-md border place-items-center hover:scale-105 transition-transform"
          style={{ borderColor: "var(--yalola-border)" }} data-testid="stories-nav-right">
          <ChevronRight size={18} style={{ color: "var(--yalola-text)" }} />
        </button>
      )}

      <StoryComposer open={composerOpen} onOpenChange={setComposerOpen} onCreated={load} />
      <StoryArchive open={archiveOpen} onOpenChange={setArchiveOpen} />
      {privateStory && <StoryPrivateScreen author={privateStory} onClose={() => setPrivateStory(null)} />}

      {viewer && story && (
        <div
          className="fixed inset-0 z-[70] bg-black grid place-items-center"
          onClick={closeViewer}
          data-testid="story-viewer"
        >
          {/* Unified gesture layer (WhatsApp / Instagram style): long-press =
              pause, quick tap left/right = previous/next, swipe up = react.
              Bounded vertically so it never covers the header or reply bar. */}
          <div
            className="absolute left-0 right-0 z-10"
            style={{ touchAction: "none", top: "calc(var(--sa-top, 0px) + 4rem)", bottom: "calc(var(--sa-bottom, 0px) + 8rem)" }}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={onPressStart}
            onPointerMove={onPressMove}
            onPointerUp={onPressEnd}
            onPointerCancel={onPressCancel}
            data-testid="story-gesture-layer"
          />

          {/* Delete confirmation — rendered INSIDE the viewer (z-[80]) so its buttons
              sit above the fullscreen player. A portaled dialog (z-50) hid behind it. */}
          {confirmDelete && (
            <div
              className="absolute inset-0 z-[80] flex items-center justify-center p-6"
              onClick={(e) => e.stopPropagation()}
              data-testid="story-delete-overlay"
            >
              <div
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                onClick={() => { setConfirmDelete(false); setPaused(false); }}
              />
              <div className="relative w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl" data-testid="story-delete-dialog">
                <h3 className="text-lg font-bold" style={{ color: "var(--yalola-text)" }}>{t("story.delete_title", "Supprimer cette story ?")}</h3>
                <p className="text-sm mt-1.5" style={{ color: "var(--yalola-text-muted)" }}>{t("story.delete_desc", "Cette action est définitive. La story et ses statistiques de vues seront supprimées.")}</p>
                <div className="flex justify-end gap-2 mt-5">
                  <button
                    type="button"
                    onClick={() => { setConfirmDelete(false); setPaused(false); }}
                    className="rounded-full px-4 py-2 text-sm font-semibold"
                    style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text)" }}
                    data-testid="story-delete-cancel"
                  >
                    {t("common.cancel", "Annuler")}
                  </button>
                  <button
                    type="button"
                    onClick={performDelete}
                    className="rounded-full px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700"
                    data-testid="story-delete-confirm"
                  >
                    {t("story.delete", "Supprimer")}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Swipe-up animated reaction picker */}
          {reactionPicker && (
            <div
              className="absolute inset-0 z-40 flex items-center justify-center"
              onClick={() => { setReactionPicker(false); setPaused(false); }}
              data-testid="story-reaction-picker"
            >
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
              <div className="relative flex flex-col items-center gap-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-end gap-3 sm:gap-5">
                  {QUICK_REACTIONS.map((emo, i) => (
                    <button
                      key={emo}
                      type="button"
                      onClick={() => pickReaction(emo)}
                      className="text-5xl sm:text-6xl transition-transform hover:scale-125 active:scale-90"
                      style={{ animation: "reactionPickerIn 0.28s ease-out both", animationDelay: `${i * 45}ms` }}
                      data-testid={`story-react-big-${emo}`}
                    >
                      {emo}
                    </button>
                  ))}
                </div>
                <p className="text-white/80 text-sm font-medium" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}>
                  {t("story.tap_to_react", "Touchez un emoji pour réagir")}
                </p>
              </div>
            </div>
          )}

          {/* Floating reactions rising to the author */}
          {floaters.map((f) => (
            <div
              key={f.id}
              className="pointer-events-none absolute left-1/2 bottom-28 text-6xl z-30"
              style={{ animation: "storyReactFloat 1.5s ease-out forwards" }}
              aria-hidden
            >
              {f.emoji}
            </div>
          ))}

          <div className="flex items-center justify-center gap-4" onClick={(e) => e.stopPropagation()}>
            {/* Desktop-only ‹ › arrows beside the card (hidden at the very first / last story). */}
            <button
              type="button"
              onClick={prev}
              aria-label={t("story.prev", "Story précédente")}
              className={`hidden sm:grid relative z-20 h-11 w-11 shrink-0 place-items-center rounded-full bg-white/15 text-white backdrop-blur-sm transition-colors hover:bg-white/30 ${(viewer.sIdx > 0 || viewer.gIdx > 0) ? "" : "invisible pointer-events-none"}`}
              data-testid="story-arrow-prev"
            >
              <ChevronLeft size={24} />
            </button>
            <div
              ref={cardRef}
              className="story-viewer-card overflow-hidden bg-black relative sm:rounded-2xl sm:shadow-2xl"
              data-testid="story-viewer-media"
            >
              <StoryStage
                story={story}
                videoRef={viewerVideoRef}
                muted={storyMuted}
                paused={paused}
                onVideoEnded={next}
                safeTop="4.5rem"
                safeBottom="8rem"
              />

              {/* Instagram-style progress segments */}
              <div className="absolute left-2 right-2 flex gap-1 z-10" style={{ top: "calc(var(--sa-top, 0px) + 8px)" }} data-testid="story-progress-bar" data-timed={story.media_type === "video" ? "video" : musicTimed ? "music" : "fixed"}>
                {group.stories.map((s, i) => (
                  <div key={s.id} className="h-1 flex-1 rounded-full bg-white/30 overflow-hidden">
                    {i < viewer.sIdx ? (
                      <div className="h-full bg-white" style={{ width: "100%" }} />
                    ) : i > viewer.sIdx ? null : story.media_type === "video" ? (
                      <div className="h-full bg-white" style={{ width: `${videoPct}%` }} />
                    ) : musicTimed ? (
                      <div className="h-full bg-white" style={{ width: `${musicPct}%` }} data-testid="story-progress-music" />
                    ) : (
                      <div
                        key={`seg-${viewer.gIdx}-${viewer.sIdx}`}
                        className="h-full bg-white"
                        style={{ animation: `storyProgress ${STORY_DURATION_MS}ms linear forwards`, animationPlayState: paused ? "paused" : "running" }}
                      />
                    )}
                  </div>
                ))}
              </div>

              <div className="absolute left-3 right-3 flex items-center gap-2 text-white z-10" style={{ top: "calc(var(--sa-top, 0px) + 20px)" }}>
                {group.author?.is_church ? (
                  group.author?.avatar_url ? (
                    <img src={imageVariant(group.author.avatar_url, 48)} alt="" className="h-9 w-9 rounded-full object-cover border-2 border-white shrink-0" data-testid="story-viewer-church-logo" />
                  ) : (
                    <div className="h-9 w-9 rounded-full grid place-items-center border-2 border-white shrink-0" style={{ background: "var(--yalola-primary)" }}>
                      <Church size={17} className="text-white" />
                    </div>
                  )
                ) : (
                  <Avatar className="h-9 w-9 border-2 border-white">
                    <AvatarImage src={avatarSrc(group.author)} />
                    <AvatarFallback>{group.author?.name?.[0]}</AvatarFallback>
                  </Avatar>
                )}
                <div className="min-w-0">
                  <div className="text-sm font-semibold flex items-center gap-1 truncate" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}>
                    <span className="truncate">{group.author?.name}</span>
                    {group.author?.is_church && <ShieldCheck size={14} className="shrink-0" />}
                  </div>
                  <div className="text-[11px] opacity-90 leading-tight truncate" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.5)" }} data-testid="story-viewer-time">
                    {group.author?.is_church && <>{t("story.official_church", "Église officielle")} · </>}{storyTimeLabel(story.created_at, lang, t)}
                  </div>
                  {story.visibility === "close_friends" && (
                    <div className="mt-0.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold text-white whitespace-nowrap" style={{ background: CLOSE_FRIENDS_COLOR }} data-testid="story-viewer-close-friends-badge">
                      <Star size={10} fill="currentColor" /> {t("close_friends.badge", "Amis proches")}
                    </div>
                  )}
                  {story.is_hidden && (
                    <div className="mt-0.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold text-white whitespace-nowrap" style={{ background: "#B91C1C" }} data-testid="story-viewer-hidden-badge">
                      <EyeOff size={10} /> {t("story.hidden_mod_badge", "Masquée · visible par les modérateurs seulement")}
                    </div>
                  )}
                </div>
                <div className="ml-auto flex items-center gap-2 shrink-0">
                  {(story.media_type === "video" || story.music_url) && (
                    <button onClick={() => setStoryMuted((m) => !m)} className="text-white bg-black/40 rounded-full h-9 w-9 grid place-items-center" data-testid="story-sound-toggle">
                      {storyMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                    </button>
                  )}
                  {user && !isOwn && !group.author?.is_church && (
                    <button onClick={openReport} className="text-white bg-black/40 hover:bg-black/60 rounded-full h-9 w-9 grid place-items-center transition-colors" data-testid="story-report" aria-label={t("post.report", "Signaler")}>
                      <Flag size={16} />
                    </button>
                  )}
                  <button onClick={closeViewer} className="text-white bg-black/40 hover:bg-black/60 rounded-full h-9 w-9 grid place-items-center transition-colors" data-testid="story-close">
                    <X size={18} />
                  </button>
                </div>
              </div>

              {/* Music badge + @mentions overlay */}
              {(story.music_url || (story.mentions || []).length > 0) && (
                <div className="absolute left-3 right-3 z-10 flex flex-col gap-1.5 items-start" style={{ top: "calc(var(--sa-top, 0px) + 64px)" }} onClick={(e) => e.stopPropagation()}>
                  {story.music_url && (
                    // Lot 680 — titre nettoyé + retour à la ligne autorisé (2 lignes max, largeur ≤ écran) : plus de débordement.
                    <span className="inline-flex items-start gap-1.5 rounded-2xl bg-black/50 text-white px-2.5 py-1 text-xs font-semibold max-w-full" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.5)" }} data-testid="story-music-badge">
                      <span className={`eq mt-0.5 shrink-0 ${!storyMuted && !paused ? "eq-on" : ""}`} aria-hidden="true" data-testid="story-music-eq"><i /><i /><i /><i /></span>
                      <span className="min-w-0 line-clamp-2 [overflow-wrap:anywhere]" data-testid="story-music-title">{prettyMusicTitle(story.music_title) || t("story.music", "Musique")}</span>
                    </span>
                  )}
                  {(story.mentions || []).length > 0 && (
                    <div className="flex flex-wrap gap-1" data-testid="story-mentions-view">
                      {story.mentions.map((m) => (
                        <button key={m.id} type="button" onClick={() => { closeViewer(); nav(`/profile/${m.id}`); }} className="rounded-full bg-white/20 backdrop-blur-sm text-white px-2 py-0.5 text-xs font-semibold hover:bg-white/30" data-testid={`story-mention-view-${m.id}`}>
                          @{m.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <audio ref={musicRef} className="hidden" data-testid="story-music-audio" />

              {/* "Seen by" + audience (lot 651) + delete for own stories */}
              {isOwn && (
                <>
                  <div className="absolute left-3 right-14 z-10 flex items-center gap-1.5 flex-wrap" style={{ bottom: "calc(var(--sa-bottom, 0px) + 12px)" }}>
                    <button
                      type="button"
                      onClick={openViewers}
                      className="flex items-center gap-1.5 rounded-full bg-black/50 text-white px-3 py-1.5 text-xs font-semibold"
                      data-testid="story-views-btn"
                    >
                      <Eye size={14} /> {t("story.viewed_by", "Vu par {count}").replace("{count}", story.views_count ?? 0)}
                    </button>
                    {!story.is_church_story && (() => { const a = audienceOf(story.visibility); return (
                      <button
                        type="button"
                        onClick={openAudience}
                        className="flex items-center gap-1.5 rounded-full text-white px-3 py-1.5 text-xs font-semibold"
                        style={{ background: a.key === "close_friends" ? audienceAccent(a.key) : "rgba(0,0,0,0.5)" }}
                        title={t("audience.edit", "Modifier l'audience")}
                        data-testid="story-audience-btn"
                        data-audience={a.key}
                      >
                        <a.icon size={14} /> {t(a.tkey, a.label)}
                      </button>
                    ); })()}
                  </div>
                  <button
                    type="button"
                    onClick={askDelete}
                    className="absolute right-3 z-10 flex items-center justify-center h-9 w-9 rounded-full text-white bg-black/50 hover:bg-black/70 transition-colors"
                    style={{ bottom: "calc(var(--sa-bottom, 0px) + 12px)" }}
                    data-testid="story-delete-btn"
                    aria-label={t("story.delete", "Supprimer")}
                  >
                    <Trash2 size={14} />
                  </button>
                </>
              )}

              {/* Facebook-style reply bar overlaid at the bottom (not on your own story) */}
              {user && !isOwn && (
                <div
                  className="absolute inset-x-0 bottom-0 z-10 px-3 pt-10 space-y-2"
                  style={{ background: "linear-gradient(0deg, rgba(0,0,0,0.65), transparent)", paddingBottom: "calc(var(--sa-bottom, 0px) + 12px)" }}
                  data-testid="story-reply-bar"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-center gap-3 sm:gap-4">
                    {QUICK_REACTIONS.map((e) => (
                      <button
                        key={e}
                        type="button"
                        onClick={() => sendReply(e)}
                        disabled={sendingReply}
                        className="text-3xl transition-transform hover:scale-125 active:scale-95"
                        data-testid={`story-react-${e}`}
                      >
                        {e}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") sendReply(reply); }}
                      onFocus={() => setPaused(true)}
                      onBlur={() => setPaused(false)}
                      placeholder={t("story.reply_placeholder", "Répondre...")}
                      className="flex-1 min-w-0 rounded-full bg-white/15 border border-white/40 text-white placeholder:text-white/70 px-4 py-2.5 text-sm focus:outline-none focus:border-white/70 backdrop-blur-sm"
                      data-testid="story-reply-input"
                    />
                    <button
                      type="button"
                      onClick={() => sendReply(reply)}
                      disabled={sendingReply || !reply.trim()}
                      className="h-10 w-10 rounded-full grid place-items-center text-white disabled:opacity-40 shrink-0"
                      style={{ background: "var(--yalola-primary)" }}
                      data-testid="story-reply-send"
                    >
                      <Send size={16} />
                    </button>
                  </div>
                </div>
              )}

              {/* Viewers panel */}
              {viewersPanel && (
                <div className="absolute inset-x-0 bottom-0 z-20 bg-white rounded-t-2xl max-h-[55%] flex flex-col" data-testid="story-viewers-panel">
                  <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--yalola-border)" }}>
                    <div className="text-sm font-bold flex items-center gap-1.5"><Eye size={15} /> {t("story.viewers_title", "Vu par")} ({viewersPanel.viewers.length})</div>
                    <button onClick={() => setViewersPanel(null)} className="h-7 w-7 grid place-items-center rounded-full hover:bg-[color:var(--yalola-surface-alt)]" data-testid="story-viewers-close">
                      <X size={15} />
                    </button>
                  </div>
                  <div className="overflow-y-auto px-2 py-1">
                    {viewersPanel.loading && <div className="p-4 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }}>...</div>}
                    {!viewersPanel.loading && viewersPanel.viewers.length === 0 && (
                      <div className="p-4 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="story-no-viewers">
                        {t("story.no_viewers", "Personne n'a encore vu cette story")}
                      </div>
                    )}
                    {viewersPanel.viewers.map((v) => (
                      <div key={v.id} className="flex items-center gap-2.5 px-2 py-2" data-testid={`story-viewer-item-${v.id}`}>
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={avatarSrc(v)} />
                          <AvatarFallback>{v.name?.[0]}</AvatarFallback>
                        </Avatar>
                        <div className="text-sm font-medium">{v.name}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={next}
              aria-label={t("story.next", "Story suivante")}
              className={`hidden sm:grid relative z-20 h-11 w-11 shrink-0 place-items-center rounded-full bg-white/15 text-white backdrop-blur-sm transition-colors hover:bg-white/30 ${(viewer.sIdx + 1 < (group.stories?.length || 0) || viewer.gIdx + 1 < groups.length) ? "" : "invisible pointer-events-none"}`}
              data-testid="story-arrow-next"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        </div>
      )}
      <ReportDialog
        open={reportOpen}
        onOpenChange={(v) => { setReportOpen(v); if (!v) setPaused(false); }}
        onReport={doReportStory}
        reporting={reporting}
        testidPrefix="story-report"
        title={t("story.report_title", "Signaler cette story")}
      />
      <Dialog open={audienceOpen} onOpenChange={closeAudience}>
        <DialogContent variant="sheet" className="sm:max-w-md z-[95]" overlayClassName="z-[94]" data-testid="story-audience-dialog">
          <DialogHeader>
            <DialogTitle>{t("audience.story_title", "Qui peut voir ta story ?")}</DialogTitle>
            <DialogDescription>{t("audience.edit_hint", "Le changement s'applique tout de suite, pour tous.")}</DialogDescription>
          </DialogHeader>
          <DialogBody>
            <AudienceOptions value={story?.visibility || "public"} onPick={changeAudience} t={t} tone="light" busy={audienceBusy} />
            {(story?.visibility || "public") !== "private" && <div className="mt-3"><StoryHiddenFromLink onClick={() => { setAudienceOpen(false); setHiddenOpen(true); }} testId="story-viewer-hidden-link" /></div>}
          </DialogBody>
        </DialogContent>
      </Dialog>
      <StoryHiddenFromDialog open={hiddenOpen} onOpenChange={(v) => { setHiddenOpen(v); if (!v) setPaused(false); }} />
    </div>
  );
};
